import { useRef } from 'react'
import { useT } from 'flyt/lib/language/language'

const MIN_COLUMN_WIDTH = 72

interface ResizeGesture {
  pointerId: number
  startX: number
  width: number
  pairWidth: number
}

/** A separate pointer target prevents the browser from starting header drag. */
export function ColumnResizeHandle({ label, percent, onResize }: {
  label: string
  percent: number
  onResize: (fraction: number) => void
}) {
  const t = useT('common')
  const gesture = useRef<ResizeGesture | null>(null)
  const measure = (handle: HTMLElement) => {
    const cell = handle.closest('th')
    const next = cell?.nextElementSibling
    if (!cell || !next) return null
    const width = cell.getBoundingClientRect().width
    return { width, pairWidth: width + next.getBoundingClientRect().width }
  }
  const resize = (width: number, pairWidth: number) => {
    if (pairWidth <= 0) return
    const min = Math.min(MIN_COLUMN_WIDTH, pairWidth / 2)
    onResize(Math.max(min, Math.min(pairWidth - min, width)) / pairWidth)
  }
  return <span
    role="separator" aria-orientation="vertical" tabIndex={0}
    aria-label={t('table.resizeColumn', { column: label })} aria-valuenow={Math.round(percent)}
    draggable={false}
    onDragStart={(event) => event.preventDefault()}
    onClick={(event) => event.stopPropagation()}
    onPointerDown={(event) => {
      if (event.button !== 0) return
      const measured = measure(event.currentTarget)
      if (!measured) return
      event.preventDefault()
      event.stopPropagation()
      gesture.current = { ...measured, startX: event.clientX, pointerId: event.pointerId }
      event.currentTarget.setPointerCapture(event.pointerId)
    }}
    onPointerMove={(event) => {
      const active = gesture.current
      if (active?.pointerId === event.pointerId) resize(active.width + event.clientX - active.startX, active.pairWidth)
    }}
    onPointerUp={(event) => {
      if (gesture.current?.pointerId !== event.pointerId) return
      gesture.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
    }}
    onPointerCancel={() => { gesture.current = null }}
    onLostPointerCapture={() => { gesture.current = null }}
    onKeyDown={(event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      const measured = measure(event.currentTarget)
      if (measured) resize(measured.width + (event.key === 'ArrowRight' ? 16 : -16), measured.pairWidth)
    }}
    className="absolute inset-y-0 right-0 z-10 w-2 touch-none cursor-col-resize select-none bg-transparent group-hover/column:bg-border-strong group-focus-within/column:bg-border-strong hover:bg-accent focus-visible:bg-border-strong focus-visible:outline-none"
  />
}
